import { Badge } from "@/components/ui/badge";
import { NetworkQualityBadge } from "./NetworkQualityBadge";

interface NetworkProbe {
  n?: string;
  p?: string;
  a?: number;
  ok?: number;
  s?: number;
  d?: number;
  rsrp?: number;
  rsrq?: number;
  snr?: number;
  rssi?: number;
  csq?: number;
}

interface NetworkProbeDisplayProps {
  networkProbe?: NetworkProbe;
  title: string;
}

export function NetworkProbeDisplay({ networkProbe, title }: NetworkProbeDisplayProps) {
  if (!networkProbe) {
    return (
      <div>
        <h4 className="font-medium mb-2">{title}</h4>
        <p className="text-muted-foreground text-sm">No network information available</p>
      </div>
    );
  }

  const getRATBadge = (rat?: number) => {
    switch (rat) {
      case 7: return <Badge variant="default" className="bg-green-400 hover:bg-green-500 text-white border-green-400 text-center justify-center">LTE (4G)</Badge>;
      case 2: return <Badge variant="secondary" className="bg-yellow-400 hover:bg-yellow-500 text-white border-yellow-400 text-center justify-center">UTRAN (3G)</Badge>;
      case 0: return <Badge variant="outline" className="bg-red-400 hover:bg-red-500 text-white border-red-400 text-center justify-center">GSM (2G)</Badge>;
      default: return <Badge variant="outline" className="text-center justify-center">Unknown</Badge>;
    }
  };

  const getAttachStatus = (ok?: number) => {
    return ok === 1 ? (
      <span className="text-green-600 font-medium">✓ Attached OK</span>
    ) : (
      <span className="text-red-600 font-medium">✗ No attach</span>
    );
  };

  return (
    <div>
      <h4 className="font-medium mb-3">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Operator</div>
          <div className="font-medium">{networkProbe.n || networkProbe.p || '—'}</div>
        </div>
        
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">PLMN</div>
          <div className="font-mono text-sm">{networkProbe.p || '—'}</div>
        </div>
        
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Technology</div>
          <div>{getRATBadge(networkProbe.a)}</div>
        </div>
        
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Quality</div>
          <NetworkQualityBadge network={networkProbe} />
        </div>
        
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Status</div>
          <div>{getAttachStatus(networkProbe.ok)}</div>
        </div>
        
        {networkProbe.rsrp && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">RSRP</div>
            <div className="font-mono text-sm">{networkProbe.rsrp} dBm</div>
          </div>
        )}
        
        {networkProbe.rsrq && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">RSRQ</div>
            <div className="font-mono text-sm">{networkProbe.rsrq} dB</div>
          </div>
        )}
      </div>
    </div>
  );
}